import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Package, DollarSign, Calendar, ArrowRight, Ticket, Info, ScanLine } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import axios from "axios";
import { format } from "date-fns";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_TICK = { fill: 'hsl(240, 5%, 64.9%)', fontSize: 12 };
const BAR_RADIUS = [4, 4, 0, 0];
const CHART_TOOLTIP_STYLE = {
  backgroundColor: 'hsl(240, 10%, 3.9%)',
  border: '1px solid hsl(240, 3.7%, 15.9%)',
  borderRadius: '8px',
  fontFamily: 'JetBrains Mono'
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const DENOMINATIONS = [1, 2, 3, 5, 10, 20, 30, 50];
const PACK_SIZES = { 1: 300, 2: 150, 3: 100, 5: 100, 10: 60, 20: 30, 30: 30, 50: 30 };

export default function Dashboard() {
  const navigate = useNavigate();
  const lottoScanAccess = useFeatureAccess("lotto_scan");
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entriesTab, setEntriesTab] = useState('daily');
  const [periodEntries, setPeriodEntries] = useState([]);
  const [periodLoading, setPeriodLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, chartRes] = await Promise.all([
        axios.get(`${API}/summary`),
        axios.get(`${API}/chart-data?days=14`)
      ]);
      setSummary(summaryRes.data);
      setChartData(chartRes.data.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPeriodEntries = useCallback(async (period) => {
    setPeriodLoading(true);
    try {
      const res = await axios.get(`${API}/entries/period?period=${period}`);
      setPeriodEntries(res.data.entries || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching period entries:", error);
      setPeriodEntries([]);
    } finally {
      setPeriodLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchPeriodEntries(entriesTab);
  }, [entriesTab, fetchPeriodEntries]);

  const formatCurrency = (value, asElement = false) => {
    const num = value || 0;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(Math.abs(num));
    if (num < 0) {
      return asElement
        ? <span className="text-red-500">({formatted})</span>
        : `(${formatted})`;
    }
    return asElement ? <span>{formatted}</span> : formatted;
  };

  const today = format(new Date(), "yyyy-MM-dd");

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="space-y-8"
      data-testid="dashboard"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-mono text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">{format(new Date(), "EEEE, MMMM d, yyyy")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {lottoScanAccess.canAccess && (
            <Button
              onClick={() => navigate("/lotto-scan")}
              className="flex items-center gap-2 bg-emerald-900 text-emerald-50 hover:bg-emerald-800 border border-emerald-950/40"
              data-testid="lotto-scan-btn"
            >
              <ScanLine className="w-4 h-4" />
              Lotto Scanning
            </Button>
          )}
          <Button
            onClick={() => navigate(`/entry/${today}`)}
            className="btn-primary flex items-center gap-2"
            data-testid="todays-entry-btn"
          >
            <Calendar className="w-4 h-4" />
            Today's Entry
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Inventory caveat — actual stock can vary because pack sizes differ per denomination */}
      <motion.div
        variants={itemVariants}
        className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 font-mono text-xs text-amber-200"
        data-testid="inventory-note"
      >
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-400" />
        <span>
          <span className="font-bold">Please Note:</span> Actual inventory amounts can vary because some games (e.g. $50) come in different pack quantities.
        </span>
      </motion.div>

      {/* First Day Setup Banner */}
      {summary?.isFirstTimeUser && (
        <motion.div variants={itemVariants} className="bg-emerald-800/40 border border-emerald-500/40 rounded-lg px-5 py-4 flex items-start gap-4" data-testid="first-day-setup-banner">
          <Info className="w-6 h-6 text-emerald-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-mono font-bold text-emerald-100 text-base mb-1">First Day Setup</h3>
            <p className="font-mono text-sm text-emerald-200 mb-3">
              Welcome! To get started, go to today's entry and fill in your current <span className="font-bold text-white">Running Numbers</span> and <span className="font-bold text-white">Ending Packs</span>. This sets your starting point — sales calculations will begin from your next entry.
            </p>
            <Button
              onClick={() => navigate(`/entry/${today}`)}
              className="btn-primary flex items-center gap-2"
              data-testid="first-day-start-btn"
            >
              <Calendar className="w-4 h-4" />
              Start First Entry
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>
      )}

      {/* Stats Grid */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card" data-testid="stat-today-sales">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="data-label">Today's Sales</p>
                <p className="data-value text-primary">{formatCurrency(summary?.total_sales_today, true)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-week-sales">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="data-label">This Week</p>
                <p className="data-value">{formatCurrency(summary?.total_sales_week, true)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-month-sales">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="data-label">This Month</p>
                <p className="data-value">{formatCurrency(summary?.total_sales_month, true)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-stock-value">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="data-label">Stock Value</p>
                <p className="data-value text-amber-500">{formatCurrency(summary?.total_stock_value, true)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Package className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Charts Row */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Trend Chart */}
        <Card className="border-border/50" data-testid="sales-chart">
          <CardHeader>
            <CardTitle className="font-mono text-lg">Sales Trend (14 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(160, 84%, 39%)" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(160, 84%, 39%)" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(240, 3.7%, 15.9%)" />
                  <XAxis 
                    dataKey="date" 
                    tick={CHART_TICK}
                    tickFormatter={(value) => format(new Date(value), "MMM d")}
                  />
                  <YAxis 
                    tick={CHART_TICK}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    formatter={(value) => [`$${value}`, 'Sales']}
                    labelFormatter={(label) => format(new Date(label), "MMM d, yyyy")}
                  />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    stroke="hsl(160, 84%, 39%)"
                    fill="url(#salesGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Stock Value by Denomination */}
        <Card className="border-border/50" data-testid="stock-chart">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-mono text-lg">Stock Value by Denomination</CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/stock")}
              className="text-primary"
            >
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary?.stock_by_denomination || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(240, 3.7%, 15.9%)" />
                  <XAxis 
                    dataKey="denomination" 
                    tick={CHART_TICK}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <YAxis 
                    tick={CHART_TICK}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    formatter={(value, _name, props) => {
                      const qty = props?.payload?.quantity ?? 0;
                      return [`$${Number(value).toLocaleString()} (${qty} pack${qty === 1 ? '' : 's'})`, 'Value'];
                    }}
                  />
                  <Bar dataKey="value" fill="hsl(160, 84%, 39%)" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Recent Entries */}
      <motion.div variants={itemVariants}>
        <Card className="border-border/50" data-testid="recent-entries">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-mono text-lg">Detailed Lottery Overview</CardTitle>
            <div className="flex items-center gap-1">
              {['daily', 'weekly', 'monthly', 'yearly'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setEntriesTab(tab)}
                  className={`px-3 py-1 rounded-lg font-mono text-xs transition-colors ${entriesTab === tab ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
                  data-testid={`tab-${tab}`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {periodLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : periodEntries.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="table-header">
                      <th className="p-2 text-left text-xs">{entriesTab === 'daily' ? 'Date' : entriesTab === 'weekly' ? 'Week Of' : entriesTab === 'monthly' ? 'Month' : 'Year'}</th>
                      {entriesTab !== 'daily' && <th className="p-2 text-right text-xs">Days</th>}
                      <th className="p-2 text-right text-xs">Instant Sale</th>
                      <th className="p-2 text-right text-xs">Online Sale</th>
                      <th className="p-2 text-right text-xs">Total Sale</th>
                      <th className="p-2 text-right text-xs">LVM-1</th>
                      <th className="p-2 text-right text-xs">LVM-2</th>
                      <th className="p-2 text-right text-xs">Total LVM</th>
                      <th className="p-2 text-right text-xs">Deliv Packs</th>
                      <th className="p-2 text-right text-xs">$Delivered</th>
                      <th className="p-2 text-right text-xs">Returned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periodEntries.map((entry) => {
                      // Total sale & online/instant breakdown — use freshly
                      // recomputed values from the backend when available
                      // (period endpoint includes them), else fall back to
                      // the stored total. This keeps Dashboard in lockstep
                      // with the live Daily Entry view.
                      const md = entry.machineData || {};
                      const inv = entry.inventory || {};
                      const eps = entry.savedPackSizes || {};
                      const packSize = (d) => parseInt(eps[String(d)] || eps[d]) || PACK_SIZES[d];
                      const onlinePays = (parseFloat(md.onlinePays?.machine1) || 0) + (parseFloat(md.onlinePays?.machine2) || 0);
                      const onlineTotal = (parseFloat(md.onlineTotal?.machine1) || 0) + (parseFloat(md.onlineTotal?.machine2) || 0);
                      const totalSale = entry.total_sales_amount || 0;
                      const onlineSale = entry._recomputed_online_sale != null
                        ? entry._recomputed_online_sale
                        : (onlinePays + onlineTotal);
                      const instantSale = entry._recomputed_instant_sale != null
                        ? entry._recomputed_instant_sale
                        : (totalSale - onlineSale);
                      // LVM values = packs sold via each vending machine, in dollars
                      let lvm1 = 0, lvm2 = 0;
                      let delivPacks = 0, delivDollars = 0, returnedDollars = 0;
                      DENOMINATIONS.forEach(d => {
                        const dp = parseInt(inv[d]?.delivery || inv[String(d)]?.delivery) || 0;
                        const tr = parseInt(inv[d]?.ticketReturned || inv[String(d)]?.ticketReturned) || 0;
                        const l1 = parseInt(inv[d]?.lvm || inv[String(d)]?.lvm) || 0;
                        const l2 = parseInt(inv[d]?.lvm2 || inv[String(d)]?.lvm2) || 0;
                        const ps = packSize(d);
                        delivPacks += dp;
                        delivDollars += dp * ps * d;
                        returnedDollars += tr * d;
                        lvm1 += l1 * ps * d;
                        lvm2 += l2 * ps * d;
                      });
                      // Format date label based on tab
                      // IMPORTANT: parse 'YYYY-MM-DD' as a LOCAL date (not UTC) to
                      // avoid the timezone shift bug where new Date('2026-06-09')
                      // is parsed as UTC midnight and displays as Jun 8 in west-of-UTC
                      // timezones — then clicking would open Jun 9 (off-by-one).
                      const parseLocalDate = (s) => {
                        const [yy, mm, dd] = s.split('-').map(Number);
                        return new Date(yy, mm - 1, dd);
                      };
                      let dateLabel = entry.date;
                      if (entriesTab === 'daily') dateLabel = format(parseLocalDate(entry.date), "MMM d");
                      else if (entriesTab === 'weekly') dateLabel = format(parseLocalDate(entry.date), "MMM d");
                      else if (entriesTab === 'monthly') { const [y, m] = entry.date.split('-'); dateLabel = format(new Date(parseInt(y), parseInt(m) - 1), "MMM yyyy"); }
                      else dateLabel = entry.date;
                      return (
                        <tr key={entry.date} className={`table-row border-b border-border/30 ${entriesTab === 'daily' ? 'cursor-pointer hover:bg-muted/30' : ''}`} onClick={() => entriesTab === 'daily' && navigate(`/entry/${entry.date}`)}>
                          <td className="table-cell text-xs">
                            <span className="text-foreground">{dateLabel}</span>
                          </td>
                          {entriesTab !== 'daily' && <td className="table-cell text-right text-xs text-muted-foreground">{entry.entry_count}</td>}
                          <td className="table-cell text-right text-xs text-primary">
                            {formatCurrency(instantSale, true)}
                          </td>
                          <td className="table-cell text-right text-xs text-blue-400">
                            {formatCurrency(onlineSale, true)}
                          </td>
                          <td className="table-cell text-right text-xs font-bold text-primary">
                            {formatCurrency(totalSale, true)}
                          </td>
                          <td className="table-cell text-right text-xs text-muted-foreground">
                            {formatCurrency(lvm1, true)}
                          </td>
                          <td className="table-cell text-right text-xs text-muted-foreground">
                            {formatCurrency(lvm2, true)}
                          </td>
                          <td className="table-cell text-right text-xs font-bold text-muted-foreground">
                            {formatCurrency(lvm1 + lvm2, true)}
                          </td>
                          <td className="table-cell text-right text-xs text-green-400">
                            {delivPacks}
                          </td>
                          <td className="table-cell text-right text-xs text-green-400">
                            {formatCurrency(delivDollars, true)}
                          </td>
                          <td className="table-cell text-right text-xs text-amber-400">
                            {formatCurrency(returnedDollars, true)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Ticket className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No entries yet. Start by adding today's entry!</p>
                <Button
                  onClick={() => navigate(`/entry/${today}`)}
                  className="mt-4 btn-primary"
                >
                  Add First Entry
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
